//src/pages/cadastros/TenantFormPage.tsx
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  HardDrive,
  Image as ImageIcon,
  Loader2,
  Mail,
  MapPin,
  Palette,
  Phone,
  Plus,
  Save,
  Star,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  BRAZILIAN_STATES,
  fetchViaCep,
} from "@/services/viacep";
import {
  useCreateTenant,
  useTenantDetail,
  useTenantSmtpSettings,
  useTestTenantSmtpSettings,
  useUpdateTenant,
  useUpsertTenantSmtpSettings,
} from "@/hooks/useTenants";
import {
  maskBRPhone,
  maskCEP,
  maskCNPJ,
  normalizeCepToApi,
} from "@/lib/masks";
import { useAuth } from "@/contexts/AuthContext";
import { PERMISSIONS } from "@/lib/permissions";
import { PermissionButton } from "@/components/shared/PermissionButton";

type ContactKind = "email" | "phone" | "whatsapp" | "other";
type ContactForm = { kind: ContactKind; value: string; is_primary: boolean };

type AddressForm = {
  label: string;
  zip: string;
  street: string;
  number: string;
  complement: string;
  district: string;
  city: string;
  state: string;
  country: string;
  is_primary: boolean;
};

type DeleteTarget =
  | { type: "contact"; index: number }
  | { type: "address"; index: number }
  | null;

const CONTACT_KIND_LABELS: Record<ContactKind, string> = {
  email: "E-mail",
  whatsapp: "WhatsApp",
  phone: "Telefone",
  other: "Outro",
};

const emptyAddress = (): AddressForm => ({
  label: "Principal",
  zip: "",
  street: "",
  number: "",
  complement: "",
  district: "",
  city: "",
  state: "",
  country: "BR",
  is_primary: true,
});

function slugify(value: string) {
  return (value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 120);
}

function formatContactValue(kind: ContactKind, value: string) {
  if (kind === "phone" || kind === "whatsapp") return maskBRPhone(value);
  return value;
}

function dataUrlToPhotoPayload(dataUrl: string | null) {
  if (!dataUrl?.startsWith("data:")) return null;

  const [meta, base64] = dataUrl.split(",");
  if (!meta || !base64) return null;

  const mimeMatch = meta.match(/^data:(.*?);base64$/);
  const mime = mimeMatch?.[1] || null;
  if (!mime) return null;

  return {
    photo_base64: base64,
    photo_mime_type: mime,
    photo_file_name: "tenant-logo",
  };
}

export default function TenantFormPage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const { user } = useAuth();

  const isCreating = !id;
  const isEditing = !!id && location.pathname.endsWith("/editar");
  const isViewing = !!id && !location.pathname.endsWith("/editar");
  const isReadOnly = isViewing;
  const canWriteTenants = user?.permissions?.includes(PERMISSIONS.PLATFORM_TENANTS_WRITE) ?? false;
  const canManageSmtp = user?.permissions?.includes(PERMISSIONS.PLATFORM_SMTP_MANAGE) ?? false;

  const fileInputRef = useRef<HTMLInputElement>(null);

  const detailQuery = useTenantDetail(id);
  const smtpSettingsQuery = useTenantSmtpSettings(id, !!id && canManageSmtp);
  const createMut = useCreateTenant();
  const updateMut = useUpdateTenant();
  const upsertTenantSmtpMut = useUpsertTenantSmtpSettings(id);
  const testTenantSmtpMut = useTestTenantSmtpSettings(id);

  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  const [form, setForm] = useState({
    name: "",
    legal_name: "",
    cnpj: "",
    slug: "",
    domain: "",
    state_registration: "",
    municipal_registration: "",
    primary_color: "#4F46E5",
    secondary_color: "#06B6D4",
    timezone: "America/Sao_Paulo",
    locale: "pt-BR",
    is_active: true,
    document_storage_limit_mb: 50,
  });

  const [contacts, setContacts] = useState<ContactForm[]>([
    { kind: "email", value: "", is_primary: true },
  ]);

  const [addresses, setAddresses] = useState<AddressForm[]>([emptyAddress()]);
  const [smtpForm, setSmtpForm] = useState({
    host: "",
    port: 587,
    username: "",
    password: "",
    security_mode: "starttls" as "ssl_tls" | "starttls" | "none",
    sender_name: "",
    sender_email: "",
    reply_to: "",
    connection_timeout_seconds: 20,
    batch_size: 25,
    rate_limit_per_minute: 120,
    is_active: true,
  });

  const [smtpTestModalOpen, setSmtpTestModalOpen] = useState(false);
  const [smtpTestEmail, setSmtpTestEmail] = useState("");
  const [activeTenantTab, setActiveTenantTab] = useState<"cadastro" | "configuracoes">("cadastro");

  useEffect(() => {
    if (!canManageSmtp && activeTenantTab === "configuracoes") {
      setActiveTenantTab("cadastro");
    }
  }, [activeTenantTab, canManageSmtp]);

  useEffect(() => {
    if (!detailQuery.data) return;

    const { tenant, person, contacts: apiContacts, addresses: apiAddresses } = detailQuery.data;

    setForm({
      name: person.name || "",
      legal_name: person.legal_name || "",
      cnpj: person.document_number ? maskCNPJ(person.document_number) : "",
      slug: tenant.slug || "",
      domain: tenant.domain || "",
      state_registration: tenant.state_registration || "",
      municipal_registration: tenant.municipal_registration || "",
      primary_color: tenant.primary_color || "#4F46E5",
      secondary_color: tenant.secondary_color || "#06B6D4",
      timezone: tenant.timezone || "America/Sao_Paulo",
      locale: tenant.locale || "pt-BR",
      is_active: tenant.is_active ?? true,
      document_storage_limit_mb: tenant.document_storage_limit_mb ?? 50,
    });

    setContacts(
      apiContacts.length
        ? apiContacts.map((c) => ({
            kind: c.kind,
            value: c.kind === "phone" || c.kind === "whatsapp" ? maskBRPhone(c.value) : c.value,
            is_primary: c.is_primary,
          }))
        : [{ kind: "email", value: "", is_primary: true }],
    );

    setAddresses(
      apiAddresses.length
        ? apiAddresses.map((a, index) => ({
            label: a.label || "Principal",
            zip: a.zip ? maskCEP(a.zip) : "",
            street: a.street || "",
            number: a.number || "",
            complement: a.complement || "",
            district: a.district || "",
            city: a.city || "",
            state: a.state || "",
            country: a.country || "BR",
            is_primary: a.is_primary || index === 0,
          }))
        : [emptyAddress()],
    );

    if (person.photo?.base64 && person.photo?.mime_type) {
      setLogoPreview(`data:${person.photo.mime_type};base64,${person.photo.base64}`);
    } else {
      setLogoPreview(null);
    }
  }, [detailQuery.data]);

  useEffect(() => {
    if (!smtpSettingsQuery.data) return;

    setSmtpForm({
      host: smtpSettingsQuery.data.host || "",
      port: smtpSettingsQuery.data.port || 587,
      username: smtpSettingsQuery.data.username || "",
      password: "",
      security_mode: smtpSettingsQuery.data.security_mode || "starttls",
      sender_name: smtpSettingsQuery.data.sender_name || "",
      sender_email: smtpSettingsQuery.data.sender_email || "",
      reply_to: smtpSettingsQuery.data.reply_to || "",
      connection_timeout_seconds: smtpSettingsQuery.data.connection_timeout_seconds || 20,
      batch_size: smtpSettingsQuery.data.batch_size || 25,
      rate_limit_per_minute: smtpSettingsQuery.data.rate_limit_per_minute || 120,
      is_active: smtpSettingsQuery.data.is_active,
    });
  }, [smtpSettingsQuery.data]);

  useEffect(() => {
    const primaryEmail = contacts.find((contact) => contact.kind === "email" && contact.value?.trim())?.value?.trim() || "";
    if (primaryEmail) {
      setSmtpTestEmail(primaryEmail);
    }
  }, [contacts]);


  const busy = detailQuery.isLoading || createMut.isPending || updateMut.isPending;

  const primaryAddressCount = useMemo(
    () => addresses.filter((a) => a.is_primary).length,
    [addresses],
  );

  function goBack() {
    navigate("/cadastros/empresa");
  }

  function handleLogoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    if (isReadOnly) return;

    const file = e.target.files?.[0];
    if (!file) return;

    if (!["image/jpeg", "image/png"].includes(file.type)) {
      toast.error("Use JPG ou PNG.");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error("Máximo 2MB.");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => setLogoPreview(reader.result as string);
    reader.readAsDataURL(file);
  }

  function removeLogo() {
    if (isReadOnly) return;
    setLogoPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  function addContact() {
    if (isReadOnly) return;
    setContacts((prev) => [...prev, { kind: "whatsapp", value: "", is_primary: false }]);
  }

  function removeContact(idx: number) {
    if (isReadOnly) return;
    setContacts((prev) => {
      const next = [...prev];
      next.splice(idx, 1);
      if (next.length > 0 && !next.some((c) => c.is_primary)) next[0].is_primary = true;
      return next.length ? next : [{ kind: "email", value: "", is_primary: true }];
    });
  }

  function setPrimaryContact(idx: number) {
    if (isReadOnly) return;
    setContacts((prev) => prev.map((c, i) => ({ ...c, is_primary: i === idx })));
  }

  function updateContact(idx: number, field: keyof ContactForm, value: any) {
    if (isReadOnly) return;
    setContacts((prev) => {
      const next = [...prev];
      if (field === "kind") {
        next[idx] = {
          ...next[idx],
          kind: value as ContactKind,
          value: formatContactValue(value as ContactKind, next[idx].value),
        };
      } else {
        next[idx] = {
          ...next[idx],
          [field]:
            field === "value" ? formatContactValue(next[idx].kind, value) : value,
        };
      }
      return next;
    });
  }

  function addAddress() {
    if (isReadOnly) return;
    setAddresses((prev) => [...prev, { ...emptyAddress(), is_primary: false }]);
  }

  function removeAddress(idx: number) {
    if (isReadOnly) return;
    setAddresses((prev) => {
      const next = [...prev];
      next.splice(idx, 1);
      if (next.length > 0 && !next.some((a) => a.is_primary)) next[0].is_primary = true;
      return next.length ? next : [emptyAddress()];
    });
  }

  function setPrimaryAddress(idx: number) {
    if (isReadOnly) return;
    setAddresses((prev) => prev.map((a, i) => ({ ...a, is_primary: i === idx })));
  }

  function setAddressField(idx: number, key: keyof AddressForm, value: string | boolean) {
    if (isReadOnly) return;
    setAddresses((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [key]: value } as AddressForm;
      return next;
    });
  }

  async function handleCepBlur(addrIdx: number) {
    if (isReadOnly) return;

    const cep = normalizeCepToApi(addresses[addrIdx]?.zip || "");
    if (!cep || cep.length !== 8) return;

    const data = await fetchViaCep(cep);

    if (!data) {
      toast.info("Não foi possível consultar o CEP agora. Você pode preencher o endereço manualmente.");
      return;
    }

    setAddresses((prev) => {
      const next = [...prev];
      const cur = next[addrIdx] || emptyAddress();

      next[addrIdx] = {
        ...cur,
        zip: maskCEP(data.cep || cur.zip),
        street: data.logradouro || cur.street,
        complement: data.complemento || cur.complement,
        district: data.bairro || cur.district,
        city: data.localidade || cur.city,
        state: (data.uf || cur.state).toUpperCase(),
        country: "BR",
      };

      return next;
    });
  }

  function confirmDeleteTarget() {
    if (!deleteTarget || isReadOnly) return;

    if (deleteTarget.type === "contact") {
      removeContact(deleteTarget.index);
      toast.success("Contato removido");
    } else {
      removeAddress(deleteTarget.index);
      toast.success("Endereço removido");
    }

    setDeleteTarget(null);
  }

  async function handleSaveSmtp() {
    if (!id || isReadOnly || !canManageSmtp) return;

    try {
      await upsertTenantSmtpMut.mutateAsync({
        ...smtpForm,
        password: smtpForm.password || undefined,
        reply_to: smtpForm.reply_to || null,
      });
      setSmtpForm((current) => ({ ...current, password: "" }));
      toast.success("Configuração SMTP salva com sucesso");
    } catch (e: any) {
      toast.error(e?.message || "Erro ao salvar configuração SMTP");
    }
  }

  async function handleOpenSmtpTestModal() {
    if (!id || isReadOnly || !canManageSmtp) return;
    setSmtpTestModalOpen(true);
  }

  async function handleTestSmtp() {
    if (!id || !canManageSmtp) return;

    const normalizedEmail = smtpTestEmail.trim();
    if (!normalizedEmail) {
      toast.error("Informe um e-mail para receber o teste SMTP");
      return;
    }

    try {
      const result = await testTenantSmtpMut.mutateAsync({ test_email: normalizedEmail });
      toast.success(result.message || "E-mail de teste SMTP enviado com sucesso");
      setSmtpTestModalOpen(false);
    } catch (e: any) {
      toast.error(e?.message || "Erro ao testar configuração SMTP");
    }
  }

  async function handleSave() {
    if (isReadOnly) return;

    if (!form.name.trim()) {
      toast.error("Informe o nome da empresa");
      return;
    }

    if (!form.legal_name.trim()) {
      toast.error("Informe a razão social");
      return;
    }

    if (!form.cnpj.trim()) {
      toast.error("Informe o CNPJ");
      return;
    }

    const validAddresses = addresses.filter((a) =>
      [a.zip, a.street, a.number, a.district, a.city, a.state].some((v) => (v || "").trim()),
    );

    if (validAddresses.length === 0) {
      toast.error("Informe ao menos um endereço");
      return;
    }

    if (primaryAddressCount === 0) {
      toast.error("Defina um endereço principal");
      return;
    }

    const body = {
      person: {
        person_type: "PJ" as const,
        name: form.name.trim(),
        legal_name: form.legal_name.trim(),
        document_number: form.cnpj || null,
        photo: dataUrlToPhotoPayload(logoPreview),
        contacts: contacts
          .filter((c) => c.value?.trim())
          .map((c) => ({
            kind: c.kind,
            value: c.value.trim(),
            is_primary: c.is_primary,
          })),
        addresses: addresses
          .filter((a) =>
            [a.zip, a.street, a.number, a.district, a.city, a.state].some((v) => (v || "").trim()),
          )
          .map((a) => ({
            label: a.label || null,
            zip: a.zip || null,
            street: a.street || null,
            number: a.number || null,
            complement: a.complement || null,
            district: a.district || null,
            city: a.city || null,
            state: a.state || null,
            country: a.country || "BR",
            is_primary: a.is_primary,
          })),
      },
      slug: slugify(form.slug || form.name),
      domain: form.domain || null,
      state_registration: form.state_registration || null,
      municipal_registration: form.municipal_registration || null,
      primary_color: form.primary_color || "#4F46E5",
      secondary_color: form.secondary_color || "#06B6D4",
      timezone: form.timezone || "America/Sao_Paulo",
      locale: form.locale || "pt-BR",
      is_active: form.is_active,
      document_storage_limit_mb: Number(form.document_storage_limit_mb || 50),
    };

    try {
      if (isEditing && id) {
        await updateMut.mutateAsync({ id, body });
        toast.success("Empresa atualizada com sucesso");
      } else if (isCreating) {
        const created = await createMut.mutateAsync(body);
        toast.success("Empresa cadastrada com sucesso");
        navigate(`/cadastros/empresa/${created.id}/onboarding`);
        return;
      }

      navigate("/cadastros/empresa");
    } catch (e: any) {
      toast.error(e?.message || "Erro ao salvar empresa");
    }
  }

  return (
    <div className="pb-10">
      <div className="sticky top-14 z-20 -mx-1 mb-6 border-b bg-background/95 px-1 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex flex-col gap-3 py-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-start gap-3">
            <Button variant="outline" size="icon" onClick={goBack} disabled={busy}>
              <ArrowLeft className="h-4 w-4" />
            </Button>

            <div>
              <h1 className="text-xl font-bold tracking-tight">
                {isViewing ? "Visualizar Empresa" : isEditing ? "Editar Empresa" : "Nova Empresa"}
              </h1>
              <p className="text-sm text-muted-foreground">
                {isViewing
                  ? "Consulta dos dados da empresa"
                  : "Cadastro de empresa com dados gerais, branding, contatos e endereço"}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 self-end md:self-auto">
            <Button variant="outline" onClick={goBack} disabled={busy}>
              {isViewing ? "Voltar" : "Cancelar"}
            </Button>
            {isViewing ? (
              <PermissionButton
                permission={PERMISSIONS.PLATFORM_TENANTS_WRITE}
                onClick={() => navigate(`/cadastros/empresa/${id}/editar`)}
              >
                Editar
              </PermissionButton>
            ) : (
              <Button onClick={handleSave} disabled={busy}>
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                Salvar
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-6">
        <div className="rounded-2xl border bg-card p-1 shadow-sm">
          <div className={`grid gap-1 ${canManageSmtp ? "sm:grid-cols-2" : ""}`}>
            <button
              type="button"
              onClick={() => setActiveTenantTab("cadastro")}
              className={`rounded-xl px-4 py-3 text-sm font-medium transition-all ${
                activeTenantTab === "cadastro"
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
              }`}
            >
              Cadastro
            </button>
            {canManageSmtp ? (
              <button
                type="button"
                onClick={() => setActiveTenantTab("configuracoes")}
                className={`rounded-xl px-4 py-3 text-sm font-medium transition-all ${
                  activeTenantTab === "configuracoes"
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                }`}
              >
                Configurações
              </button>
            ) : null}
          </div>
        </div>

        {activeTenantTab === "cadastro" ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Building2 className="h-4 w-4 text-primary" />
              Dados da Empresa
            </CardTitle>
          </CardHeader>

          <CardContent className="space-y-4">
            <div className="flex flex-col gap-6 lg:flex-row">
              <div className="w-full shrink-0 lg:w-56">
                <Label className="mb-2 block">Logo</Label>

                <div className="rounded-xl border border-dashed p-3">
                  <div className="flex aspect-square items-center justify-center overflow-hidden rounded-lg border bg-muted/30">
                    {logoPreview ? (
                      <img
                        src={logoPreview}
                        alt="Logo"
                        className="h-full w-full object-contain"
                      />
                    ) : (
                      <div className="flex flex-col items-center gap-2 text-muted-foreground">
                        <ImageIcon className="h-8 w-8" />
                        <span className="px-2 text-center text-xs">JPG ou PNG até 2MB</span>
                      </div>
                    )}
                  </div>

                  {!isReadOnly ? (
                    <div className="mt-3 flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        className="flex-1"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isReadOnly}
                      >
                        <ImageIcon className="mr-2 h-4 w-4" />
                        {logoPreview ? "Trocar" : "Enviar"}
                      </Button>

                      {logoPreview ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={removeLogo}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      ) : null}
                    </div>
                  ) : null}

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png"
                    className="hidden"
                    onChange={handleLogoUpload}
                    disabled={isReadOnly}
                  />
                </div>
              </div>

              <div className="grid flex-1 grid-cols-1 gap-4 md:grid-cols-2">
                <div className="md:col-span-2">
                  <Label>Nome da Empresa *</Label>
                  <Input
                    value={form.name}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        name: e.target.value,
                        slug: prev.slug ? prev.slug : slugify(e.target.value),
                      }))
                    }
                    className="mt-1"
                    disabled={isReadOnly}
                  />
                </div>

                <div>
                  <Label>Razão Social *</Label>
                  <Input
                    value={form.legal_name}
                    onChange={(e) => setForm((prev) => ({ ...prev, legal_name: e.target.value }))}
                    className="mt-1"
                    disabled={isReadOnly}
                  />
                </div>

                <div>
                  <Label>CNPJ *</Label>
                  <Input
                    value={form.cnpj}
                    onChange={(e) => setForm((prev) => ({ ...prev, cnpj: maskCNPJ(e.target.value) }))}
                    placeholder="00.000.000/0001-00"
                    className="mt-1"
                    disabled={isReadOnly}
                  />
                </div>

                <div>
                  <Label>Slug</Label>
                  <Input
                    value={form.slug}
                    onChange={(e) => setForm((prev) => ({ ...prev, slug: slugify(e.target.value) }))}
                    className="mt-1"
                    disabled={isReadOnly}
                  />
                </div>

                <div>
                  <Label>Domínio</Label>
                  <Input
                    value={form.domain}
                    onChange={(e) => setForm((prev) => ({ ...prev, domain: e.target.value }))}
                    className="mt-1"
                    placeholder="app.suaempresa.com.br"
                    disabled={isReadOnly}
                  />
                </div>

                <div>
                  <Label>Inscrição Estadual</Label>
                  <Input
                    value={form.state_registration}
                    onChange={(e) => setForm((prev) => ({ ...prev, state_registration: e.target.value }))}
                    className="mt-1"
                    disabled={isReadOnly}
                  />
                </div>

                <div>
                  <Label>Inscrição Municipal</Label>
                  <Input
                    value={form.municipal_registration}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, municipal_registration: e.target.value }))
                    }
                    className="mt-1"
                    disabled={isReadOnly}
                  />
                </div>

                <div>
                  <Label>Fuso Horário</Label>
                  <Select
                    value={form.timezone}
                    onValueChange={(value) => setForm((prev) => ({ ...prev, timezone: value }))}
                    disabled={isReadOnly}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="America/Sao_Paulo">America/Sao_Paulo</SelectItem>
                      <SelectItem value="America/Manaus">America/Manaus</SelectItem>
                      <SelectItem value="America/Fortaleza">America/Fortaleza</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Idioma</Label>
                  <Select
                    value={form.locale}
                    onValueChange={(value) => setForm((prev) => ({ ...prev, locale: value }))}
                    disabled={isReadOnly}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pt-BR">Português (Brasil)</SelectItem>
                      <SelectItem value="en-US">English (US)</SelectItem>
                      <SelectItem value="es-ES">Español</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        ) : null}

        {activeTenantTab === "cadastro" ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Palette className="h-4 w-4 text-primary" />
              Branding
            </CardTitle>
          </CardHeader>

          <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <Label>Cor Primária</Label>
              <div className="mt-1 flex items-center gap-3">
                <input
                  type="color"
                  value={form.primary_color}
                  onChange={(e) => setForm((prev) => ({ ...prev, primary_color: e.target.value }))}
                  className="h-10 w-14 cursor-pointer rounded border"
                  disabled={isReadOnly}
                />
                <Input
                  value={form.primary_color}
                  onChange={(e) => setForm((prev) => ({ ...prev, primary_color: e.target.value }))}
                  disabled={isReadOnly}
                />
              </div>
            </div>

            <div>
              <Label>Cor Secundária</Label>
              <div className="mt-1 flex items-center gap-3">
                <input
                  type="color"
                  value={form.secondary_color}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, secondary_color: e.target.value }))
                  }
                  className="h-10 w-14 cursor-pointer rounded border"
                  disabled={isReadOnly}
                />
                <Input
                  value={form.secondary_color}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, secondary_color: e.target.value }))
                  }
                  disabled={isReadOnly}
                />
              </div>
            </div>
          </CardContent>
        </Card>
        ) : null}

        {canManageSmtp && activeTenantTab === "configuracoes" ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <HardDrive className="h-4 w-4 text-primary" />
              Armazenamento de Documentos
            </CardTitle>
          </CardHeader>

          <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <Label>Limite por tenant (MB)</Label>
              <Input
                type="number"
                min={1}
                max={102400}
                value={form.document_storage_limit_mb}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    document_storage_limit_mb: Number(e.target.value || 50),
                  }))
                }
                className="mt-1"
                disabled={isReadOnly}
              />
              <p className="mt-2 text-xs text-muted-foreground">
                Define o espaço disponível para uploads no módulo Documentos. O padrão do MVP é 50 MB por tenant.
              </p>
            </div>
          </CardContent>
        </Card>
        ) : null}

        {activeTenantTab === "cadastro" ? (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base">
              <Phone className="h-4 w-4 text-primary" />
              Contatos
            </CardTitle>

            {!isReadOnly ? (
              <Button variant="outline" size="sm" onClick={addContact}>
                <Plus className="mr-2 h-4 w-4" />
                Adicionar
              </Button>
            ) : null}
          </CardHeader>

          <CardContent className="space-y-3">
            {contacts.map((c, idx) => (
              <div key={idx} className="rounded-lg border p-3">
                <div className="grid grid-cols-1 gap-3 md:grid-cols-[180px_1fr_auto_auto] md:items-end">
                  <div>
                    <Label>Tipo</Label>
                    <Select
                      value={c.kind}
                      onValueChange={(v) => updateContact(idx, "kind", v)}
                      disabled={isReadOnly}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(CONTACT_KIND_LABELS).map(([key, label]) => (
                          <SelectItem key={key} value={key}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Valor</Label>
                    <Input
                      value={c.value}
                      onChange={(e) => updateContact(idx, "value", e.target.value)}
                      className="mt-1"
                      disabled={isReadOnly}
                    />
                  </div>

                  {!isReadOnly ? (
                    <>
                      <Button
                        type="button"
                        variant={c.is_primary ? "default" : "outline"}
                        size="sm"
                        onClick={() => setPrimaryContact(idx)}
                      >
                        <Star className={`mr-2 h-4 w-4 ${c.is_primary ? "fill-current" : ""}`} />
                        Principal
                      </Button>

                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        disabled={contacts.length === 1}
                        onClick={() => setDeleteTarget({ type: "contact", index: idx })}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </>
                  ) : (
                    <div className="md:col-span-2" />
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
        ) : null}

        {activeTenantTab === "cadastro" ? (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base">
              <MapPin className="h-4 w-4 text-primary" />
              Endereços
            </CardTitle>

            {!isReadOnly ? (
              <Button variant="outline" size="sm" onClick={addAddress}>
                <Plus className="mr-2 h-4 w-4" />
                Adicionar
              </Button>
            ) : null}
          </CardHeader>

          <CardContent className="space-y-4">
            {addresses.map((a, idx) => (
              <div key={idx} className="rounded-xl border p-4">
                <div className="mb-4 flex flex-wrap items-center gap-2">
                  <Input
                    value={a.label}
                    onChange={(e) => setAddressField(idx, "label", e.target.value)}
                    className="h-9 w-40"
                    placeholder="Rótulo"
                    disabled={isReadOnly}
                  />

                  {!isReadOnly ? (
                    <>
                      <Button
                        type="button"
                        variant={a.is_primary ? "default" : "outline"}
                        size="sm"
                        onClick={() => setPrimaryAddress(idx)}
                      >
                        <Star className={`mr-2 h-4 w-4 ${a.is_primary ? "fill-current" : ""}`} />
                        Principal
                      </Button>

                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        disabled={addresses.length === 1}
                        onClick={() => setDeleteTarget({ type: "address", index: idx })}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </>
                  ) : null}
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-12">
                  <div className="md:col-span-4">
                    <Label>CEP</Label>
                    <Input
                      value={a.zip}
                      onChange={(e) => setAddressField(idx, "zip", maskCEP(e.target.value))}
                      onBlur={() => handleCepBlur(idx)}
                      className="mt-1"
                      disabled={isReadOnly}
                    />
                  </div>

                  <div className="hidden md:col-span-8 md:block" />

                  <div className="md:col-span-6">
                    <Label>Rua</Label>
                    <Input
                      value={a.street}
                      onChange={(e) => setAddressField(idx, "street", e.target.value)}
                      className="mt-1"
                      disabled={isReadOnly}
                    />
                  </div>

                  <div className="md:col-span-2">
                    <Label>Número</Label>
                    <Input
                      value={a.number}
                      onChange={(e) => setAddressField(idx, "number", e.target.value)}
                      className="mt-1"
                      disabled={isReadOnly}
                    />
                  </div>

                  <div className="md:col-span-4">
                    <Label>Complemento</Label>
                    <Input
                      value={a.complement}
                      onChange={(e) => setAddressField(idx, "complement", e.target.value)}
                      className="mt-1"
                      disabled={isReadOnly}
                    />
                  </div>

                  <div className="md:col-span-4">
                    <Label>Bairro</Label>
                    <Input
                      value={a.district}
                      onChange={(e) => setAddressField(idx, "district", e.target.value)}
                      className="mt-1"
                      disabled={isReadOnly}
                    />
                  </div>

                  <div className="md:col-span-4">
                    <Label>Cidade</Label>
                    <Input
                      value={a.city}
                      onChange={(e) => setAddressField(idx, "city", e.target.value)}
                      className="mt-1"
                      disabled={isReadOnly}
                    />
                  </div>

                  <div className="md:col-span-4">
                    <Label>Estado</Label>
                    <Select
                      value={a.state || ""}
                      onValueChange={(value) => setAddressField(idx, "state", value)}
                      disabled={isReadOnly}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="UF" />
                      </SelectTrigger>
                      <SelectContent>
                        {BRAZILIAN_STATES.map((uf) => (
                          <SelectItem key={uf.value} value={uf.value}>
                            {uf.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
        ) : null}
        {canManageSmtp && activeTenantTab === "configuracoes" ? (
        <Card>
          <CardHeader className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <Mail className="h-4 w-4 text-primary" />
                E-mail SMTP
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Configuração institucional do tenant para disparo dos convites por e-mail.
              </p>
            </div>

            {id && !isReadOnly ? (
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="outline"
                  onClick={handleOpenSmtpTestModal}
                  disabled={testTenantSmtpMut.isPending || upsertTenantSmtpMut.isPending}
                >
                  {testTenantSmtpMut.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />}
                  Testar SMTP
                </Button>
                <Button
                  onClick={handleSaveSmtp}
                  disabled={upsertTenantSmtpMut.isPending || testTenantSmtpMut.isPending}
                >
                  {upsertTenantSmtpMut.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                  Salvar SMTP
                </Button>
              </div>
            ) : null}
          </CardHeader>

          <CardContent>
            {!id ? (
              <div className="rounded-xl border border-dashed bg-muted/30 px-4 py-6 text-sm text-muted-foreground">
                Salve a empresa primeiro para habilitar a configuração SMTP.
              </div>
            ) : (
              <div className="space-y-4">
                <div className="rounded-xl border bg-muted/20 px-4 py-3 text-sm text-muted-foreground">
                  Os convites serão enviados como <strong>no-reply</strong>, com aviso para o respondente não responder a mensagem e rodapé <strong>Powered by Normex</strong>.
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <Label>Host SMTP</Label>
                    <Input className="mt-1" value={smtpForm.host} onChange={(e) => setSmtpForm((prev) => ({ ...prev, host: e.target.value }))} disabled={isReadOnly} />
                  </div>

                  <div>
                    <Label>Porta</Label>
                    <Input className="mt-1" type="number" value={smtpForm.port} onChange={(e) => setSmtpForm((prev) => ({ ...prev, port: Number(e.target.value || 0) }))} disabled={isReadOnly} />
                  </div>

                  <div>
                    <Label>Usuário</Label>
                    <Input className="mt-1" value={smtpForm.username} onChange={(e) => setSmtpForm((prev) => ({ ...prev, username: e.target.value }))} disabled={isReadOnly} />
                  </div>

                  <div>
                    <Label>Senha SMTP</Label>
                    <Input className="mt-1" type="password" placeholder={smtpSettingsQuery.data?.has_password ? "••••••••" : "Informe a senha SMTP"} value={smtpForm.password} onChange={(e) => setSmtpForm((prev) => ({ ...prev, password: e.target.value }))} disabled={isReadOnly} />
                  </div>

                  <div>
                    <Label>Segurança</Label>
                    <Select value={smtpForm.security_mode} onValueChange={(value: "ssl_tls" | "starttls" | "none") => setSmtpForm((prev) => ({ ...prev, security_mode: value }))} disabled={isReadOnly}>
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="starttls">STARTTLS</SelectItem>
                        <SelectItem value="ssl_tls">SSL/TLS</SelectItem>
                        <SelectItem value="none">Sem criptografia</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Status do SMTP</Label>
                    <Select value={smtpForm.is_active ? "active" : "inactive"} onValueChange={(value) => setSmtpForm((prev) => ({ ...prev, is_active: value === "active" }))} disabled={isReadOnly}>
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Ativo para envio</SelectItem>
                        <SelectItem value="inactive">Inativo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Nome do remetente</Label>
                    <Input className="mt-1" value={smtpForm.sender_name} onChange={(e) => setSmtpForm((prev) => ({ ...prev, sender_name: e.target.value }))} disabled={isReadOnly} />
                  </div>

                  <div>
                    <Label>E-mail remetente</Label>
                    <Input className="mt-1" type="email" value={smtpForm.sender_email} onChange={(e) => setSmtpForm((prev) => ({ ...prev, sender_email: e.target.value }))} disabled={isReadOnly} />
                  </div>

                  <div>
                    <Label>Reply-To</Label>
                    <Input className="mt-1" type="email" value={smtpForm.reply_to || ""} onChange={(e) => setSmtpForm((prev) => ({ ...prev, reply_to: e.target.value }))} disabled={isReadOnly} />
                  </div>

                  <div>
                    <Label>Timeout de conexão (segundos)</Label>
                    <Input className="mt-1" type="number" value={smtpForm.connection_timeout_seconds} onChange={(e) => setSmtpForm((prev) => ({ ...prev, connection_timeout_seconds: Number(e.target.value || 0) }))} disabled={isReadOnly} />
                  </div>

                  <div>
                    <Label>Tamanho do lote</Label>
                    <Input className="mt-1" type="number" value={smtpForm.batch_size} onChange={(e) => setSmtpForm((prev) => ({ ...prev, batch_size: Number(e.target.value || 0) }))} disabled={isReadOnly} />
                  </div>

                  <div>
                    <Label>Limite por minuto</Label>
                    <Input className="mt-1" type="number" value={smtpForm.rate_limit_per_minute} onChange={(e) => setSmtpForm((prev) => ({ ...prev, rate_limit_per_minute: Number(e.target.value || 0) }))} disabled={isReadOnly} />
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        ) : null}

      </div>

      <Dialog open={smtpTestModalOpen} onOpenChange={setSmtpTestModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Enviar e-mail de teste SMTP</DialogTitle>
            <DialogDescription>
              Será enviado um e-mail real para validar a conexão SMTP e o fluxo completo de envio.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="smtp-test-email">E-mail de destino do teste</Label>
              <Input
                id="smtp-test-email"
                className="mt-1"
                type="email"
                placeholder="email@empresa.com"
                value={smtpTestEmail}
                onChange={(e) => setSmtpTestEmail(e.target.value)}
                disabled={testTenantSmtpMut.isPending}
              />
              <p className="mt-2 text-xs text-muted-foreground">
                O sistema vai validar a conexão SMTP e enviar um e-mail real de teste para este endereço.
              </p>
            </div>

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setSmtpTestModalOpen(false)}
                disabled={testTenantSmtpMut.isPending}
              >
                Cancelar
              </Button>
              <Button type="button" onClick={handleTestSmtp} disabled={testTenantSmtpMut.isPending}>
                {testTenantSmtpMut.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />}
                Enviar teste
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Remover item"
        description="Deseja remover este item do formulário?"
        onConfirm={confirmDeleteTarget}
        confirmLabel="Remover"
        variant="destructive"
      />
    </div>
  );
}
